// One multi-select step's fields. Flat: a prompt and one option list. Split
// (the v1 chips behaviour): two prompts, two option sets, and the threshold
// on the scored step's values that decides which set a guest sees — a score
// at or below it shows the "negative" set. Split is offered only when a
// scored choice step precedes this one (spec §3); otherwise the switch is
// disabled with the reason, and validateSteps rejects a stale split.

import { Button, Input, SegmentedControl, Select } from "@pej/kore/components";
import { Plus, X } from "lucide-react";
import { Controller, type FieldPath, type UseFormReturn, useFieldArray } from "react-hook-form";
import { Field } from "@/lib/forms";
import { OPTION_CAPTION } from "./option-caption";
import { StepDescriptionField } from "./step-description-field";
import { MAX_OPTIONS, type MultiMode, type StepsFormValues } from "./survey-schema";

export interface MultiStepFieldsProps {
    form: UseFormReturn<StepsFormValues>;
    stepIndex: number;
    /** The scored step's distinct values when one precedes this step; null
     *  means split mode is unavailable. */
    metricValues: number[] | null;
}

export function MultiStepFields({ form, stepIndex, metricValues }: MultiStepFieldsProps) {
    const mode = form.watch(`steps.${stepIndex}.multiMode`);
    const splitAvailable = metricValues !== null && metricValues.length > 0;

    return (
        <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
                <SegmentedControl<MultiMode>
                    aria-label="Multi-select mode"
                    value={mode}
                    onValueChange={(next) => form.setValue(`steps.${stepIndex}.multiMode`, next, { shouldDirty: true })}
                    options={[
                        { value: "flat", label: "One list" },
                        { value: "split", label: "Split by score", disabled: !splitAvailable },
                    ]}
                />
                {!splitAvailable && (
                    <p className="text-xs text-kore-text-muted">
                        Splitting by score needs a scored choice step earlier in the survey.
                    </p>
                )}
            </div>

            {mode === "flat" ? (
                <>
                    <Field
                        name={`steps.${stepIndex}.prompt`}
                        control={form.control}
                        label="Prompt"
                        required
                        render={({ field }) => <Input {...field} placeholder="Which dishes should we add?" />}
                    />
                    <StepDescriptionField control={form.control} name={`steps.${stepIndex}.description`} />
                    <OptionRows form={form} stepIndex={stepIndex} set="multiOptions" groupLabel="option" />
                </>
            ) : (
                <>
                    <Field
                        name={`steps.${stepIndex}.promptNegative`}
                        control={form.control}
                        label="Prompt (low scores)"
                        required
                        render={({ field }) => <Input {...field} placeholder="What went wrong?" />}
                    />
                    <StepDescriptionField
                        control={form.control}
                        name={`steps.${stepIndex}.descriptionNegative`}
                        label="Description (low scores)"
                    />
                    <OptionRows form={form} stepIndex={stepIndex} set="negative" groupLabel="negative" />
                    <Field
                        name={`steps.${stepIndex}.promptPositive`}
                        control={form.control}
                        label="Prompt (high scores)"
                        required
                        render={({ field }) => <Input {...field} placeholder="What did you like?" />}
                    />
                    <StepDescriptionField
                        control={form.control}
                        name={`steps.${stepIndex}.descriptionPositive`}
                        label="Description (high scores)"
                    />
                    <OptionRows form={form} stepIndex={stepIndex} set="positive" groupLabel="positive" />
                    <Field
                        name={`steps.${stepIndex}.threshold`}
                        control={form.control}
                        label="Threshold"
                        description="A response scoring at or below this shows the low-score options; above it shows the high-score ones."
                        render={({ field: { value, onChange, name } }) => (
                            <Select value={String(value)} onValueChange={(next) => onChange(Number(next))} name={name}>
                                <Select.Trigger aria-label="Threshold">
                                    <Select.Value>{(v: unknown) => String(v)}</Select.Value>
                                </Select.Trigger>
                                <Select.Content>
                                    {(metricValues ?? []).map((optionValue) => (
                                        <Select.Item key={optionValue} value={String(optionValue)}>
                                            {optionValue}
                                        </Select.Item>
                                    ))}
                                </Select.Content>
                            </Select>
                        )}
                    />
                </>
            )}
        </div>
    );
}

type OptionSet = "multiOptions" | "negative" | "positive";

interface OptionRowsProps {
    form: UseFormReturn<StepsFormValues>;
    stepIndex: number;
    set: OptionSet;
    groupLabel: string;
}

function OptionRows({ form, stepIndex, set, groupLabel }: OptionRowsProps) {
    const { fields, append, remove } = useFieldArray({ control: form.control, name: `steps.${stepIndex}.${set}` });
    const rowLabel = (index: number) => `Step ${stepIndex + 1} ${groupLabel} option ${index + 1}`;

    return (
        // biome-ignore lint/a11y/useSemanticElements: <fieldset> carries browser-default border/margin chrome this row list can't use; div+role="group" is the house pattern (see kore's components/assets/folder-chip).
        <div role="group" aria-label={`Step ${stepIndex + 1} ${groupLabel} options`} className="flex flex-col gap-2">
            {/* Column captions; each input carries its own row-specific name. */}
            <div aria-hidden="true" className={`flex items-center gap-2 pr-10 ${OPTION_CAPTION}`}>
                <span className="w-32">Key</span>
                <span className="min-w-0 flex-1">Label</span>
            </div>
            {fields.map((field, index) => (
                <div key={field.id} className="flex items-center gap-2">
                    <Controller
                        control={form.control}
                        name={`steps.${stepIndex}.${set}.${index}.key` as FieldPath<StepsFormValues>}
                        render={({ field: controllerField }) => (
                            <Input
                                {...controllerField}
                                value={String(controllerField.value ?? "")}
                                aria-label={`${rowLabel(index)} key`}
                                placeholder="key"
                                className="w-32"
                            />
                        )}
                    />
                    <Controller
                        control={form.control}
                        name={`steps.${stepIndex}.${set}.${index}.label` as FieldPath<StepsFormValues>}
                        render={({ field: controllerField }) => (
                            <Input
                                {...controllerField}
                                value={String(controllerField.value ?? "")}
                                aria-label={`${rowLabel(index)} label`}
                                placeholder="Label"
                                className="min-w-0 flex-1"
                            />
                        )}
                    />
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label={`Remove ${rowLabel(index).toLowerCase()}`}
                        onClick={() => remove(index)}
                        disabled={fields.length <= 1}
                    >
                        <X aria-hidden="true" />
                    </Button>
                </div>
            ))}
            <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => append({ key: `option-${fields.length + 1}`, label: "" })}
                disabled={fields.length >= MAX_OPTIONS}
            >
                <Plus aria-hidden="true" />
                Add option
            </Button>
        </div>
    );
}
